/**
 * Catching credentials the user never ticked "secret" (18.08.2026).
 *
 * Variable redaction keys on a per-variable `secret` boolean that defaults to
 * false. That is the right primary control — the user's explicit intent — but
 * it fails exactly when it matters: someone pastes a bearer token into a
 * normal variable to get unblocked, and it reaches the model provider on the
 * next `get_environments` call. The tickbox protects the careful user; this
 * protects the one in a hurry.
 *
 * Deliberately narrow. Every false positive is a variable the model can no
 * longer read, which breaks real work — so these patterns match things that
 * are almost never anything BUT a credential. A base URL, an email, a numeric
 * id and a UUID all pass through untouched.
 *
 * This never overrides the user: a variable already marked secret is redacted
 * regardless, and this only adds to that set.
 */

/**
 * Vendor key formats and structural credential shapes.
 * Sources: each vendor's published prefix, plus RFC 7519 for JWTs.
 */
const SECRET_VALUE_PATTERNS = [
  /^sk-[A-Za-z0-9_-]{16,}$/,               // OpenAI / Anthropic style
  /^sk-ant-[A-Za-z0-9_-]{16,}$/,
  /^gh[pousr]_[A-Za-z0-9]{16,}$/,          // GitHub tokens
  /^xox[baprs]-[A-Za-z0-9-]{10,}$/,        // Slack
  /^AKIA[0-9A-Z]{16}$/,                    // AWS access key id
  /^AIza[0-9A-Za-z_-]{35}$/,               // Google API key
  /^ey[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}$/, // JWT
  /^-{5}BEGIN [A-Z ]*PRIVATE KEY-{5}/,     // PEM private key
  /^[a-z]+:\/\/[^/\s:@]+:[^/\s:@]+@/i,     // URL with inline credentials
  // An HTTP auth header value with a real token after the scheme (02.10.2026:
  // `Authorization: Bearer <jwt>` was not caught — the JWT rule is anchored at
  // the start). `Bearer {{token}}` stays out: braces are not token characters.
  /^(bearer|basic|token|digest)\s+[A-Za-z0-9._~+/=-]{16,}$/i,
];

/** Names that state their own sensitivity. Anchored to avoid matching e.g. "password_field_label". */
const SECRET_NAME_PATTERN =
  /(^|[_-])(password|passwd|secret|token|api[_-]?key|apikey|private[_-]?key|credential|auth[_-]?token|access[_-]?token|refresh[_-]?token|client[_-]?secret)([_-]|$)/i;

/** Does this look like a credential even though nobody said so? */
export function looksSecret(key, value) {
  if (typeof value !== 'string' || !value.trim()) return false;

  // A name that says "token" plus a value long enough to be one. The length
  // floor keeps placeholders like `{{token}}` and "changeme" out.
  if (SECRET_NAME_PATTERN.test(String(key || '')) && value.trim().length >= 12) {
    // A template reference is a pointer, not a secret — redacting it would
    // hide the wiring the model needs to explain the request.
    if (/^\{\{[^}]+\}\}$/.test(value.trim())) return false;
    return true;
  }

  return SECRET_VALUE_PATTERNS.some((re) => re.test(value.trim()));
}

/**
 * Credential shapes as they appear INSIDE free text (18.08.2026).
 *
 * `looksSecret` answers "is this whole value a credential?" for a (key, value)
 * pair. That is the wrong question for an error string: nothing is a key/value
 * pair there, and every value pattern above is ^…$-anchored, so
 * `looksSecret('error', 'FATAL: password authentication failed for user…')`
 * is false and always will be. These are the unanchored counterparts.
 *
 * Ordered so the connection-string rule claims `postgres://u:p@h` before the
 * bare-token rules can see fragments of it.
 */
const INLINE_SECRET_PATTERNS = [
  // FIRST, and it has to be: a PEM block usually arrives introduced by a
  // credential-shaped key ("private_key: -----BEGIN…"), and the key=value rule
  // below would then consume only the `-----BEGIN` token — its value class
  // stops at whitespace — leaving the actual key material in the clear and
  // destroying the anchor this rule needs.
  [/-{5}BEGIN [A-Z ]*PRIVATE KEY-{5}[\s\S]*?(-{5}END [A-Z ]*PRIVATE KEY-{5}|$)/g, '<REDACTED PRIVATE KEY>'],
  // scheme://user:password@host. The password runs to the LAST `@` before the
  // host, because `@` and `:` are legal in a password and a class that stops at
  // the first one leaves the tail of the credential visible while still looking
  // redacted — worse than not matching at all.
  [/([a-z][a-z0-9+.-]*:\/\/)([^/\s:@]+):([^/\s]+)@(?=[^/\s@]*(?:[/?#]|$))/gi, '$1$2:<REDACTED>@'],
  // key=value / key: value where the key names a credential. Stops at a
  // delimiter so the rest of the message survives and stays readable. The
  // negative lookahead keeps it off a value an earlier rule already replaced,
  // which otherwise re-cuts `<REDACTED PRIVATE KEY>` at its first space.
  // `?` too (04.10.2026): the FIRST query parameter of a URL
  // (`/v1?api_key=…`) follows `?`, not `&`, and walked straight through.
  [/((?:^|[\s;,&(?])(?:password|passwd|pwd|secret|token|api[_-]?key|apikey|access[_-]?token|refresh[_-]?token|client[_-]?secret|auth[_-]?token|private[_-]?key)\s*[=:]\s*)(?!<REDACTED)(?:"[^"]*"|'[^']*'|[^\s;,&)]+)/gi, '$1<REDACTED>'],
  // Authorization-style schemes carrying the credential inline.
  [/\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{8,}/gi, '$1 <REDACTED>'],
  // Vendor key formats, unanchored so they are caught mid-sentence.
  [/\bsk-ant-[A-Za-z0-9_-]{16,}/g, '<REDACTED>'],
  [/\bsk-[A-Za-z0-9_-]{16,}/g, '<REDACTED>'],
  [/\bgh[pousr]_[A-Za-z0-9]{16,}/g, '<REDACTED>'],
  [/\bxox[baprs]-[A-Za-z0-9-]{10,}/g, '<REDACTED>'],
  [/\bAKIA[0-9A-Z]{16}\b/g, '<REDACTED>'],
  [/\bAIza[0-9A-Za-z_-]{35}\b/g, '<REDACTED>'],
  [/\bey[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, '<REDACTED>'],
];

/**
 * Replace credential-shaped substrings in free text.
 *
 * Built for exception messages, which are the one thing that reaches the model
 * provider without passing through a per-tool projection:
 * `condenseToolResultForApi` returns `{ error }` verbatim before its switch runs,
 * and `buildToolTurnHistoryEntry` puts 120 characters of it into the replayed
 * history. A database driver that cannot connect says so with the connection
 * string in hand, password included.
 *
 * Best-effort by construction. It recognises the shapes credentials usually
 * take, not every string that happens to be one — the guarantee remains the
 * `secret` flag on a variable, not this.
 */
export function redactSecretsInText(text) {
  if (typeof text !== 'string' || !text) return text;
  let out = text;
  for (const [pattern, replacement] of INLINE_SECRET_PATTERNS) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

/**
 * Redact a variable list for the model.
 *
 * Marks WHY a value was withheld: `<SECRET>` is the user's own choice, and
 * `<REDACTED: looks like a credential>` tells them the app made a judgement
 * call they can override by editing the variable.
 */
export function redactVariables(variables) {
  return (variables || []).map((v) => {
    if (v?.secret) return { ...v, value: '<SECRET>' };
    if (looksSecret(v?.key, v?.value)) {
      return { ...v, value: '<REDACTED: looks like a credential>', auto_redacted: true };
    }
    return v;
  });
}
